import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { Close, SearchOutlined, ExpandMore } from "@mui/icons-material";
import { useAppSelector } from "store";
import { bookingByMerchantId, updateBookingNoted } from "services/bookingApi";
import { BookingDetailsType, COLUMNS } from "./utils";
import BookingModal from "./components/BookingModal";
import ConfirmNotedDialog from "./components/ConfirmNotedDialog";
import GroupedBookingsTable from "./components/GroupedBookingsTable";
import BookingListTable from "./components/BookingListTable"; 
import { getGroupedBookings, GroupMode } from "./utils/groupingUtils";

const IndividualBooking = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const userDetails = useAppSelector(
    (state) => state.appLayout.userDetails
  ) as any;
  const organization = useAppSelector((state) => state?.merchantForm?.organisationDetails) as any;
  
  const [data, setData] = useState<any>([]);
  const [filteredData, setFilteredData] = useState<any>([]);
  const [pageNoted, setPageNoted] = useState(1);
  const [pageNew, setPageNew] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("CONFIRMED");
  const [confirmNotedDialog, setConfirmNotedDialog] = useState(false);
  const [selectedBookingToNote, setSelectedBookingToNote] = useState<any>(null);
  const [notificationMessage, setNotificationMessage] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [groupMode, setGroupMode] = useState<GroupMode>("all");

  const handleChangePage = useCallback((event: React.ChangeEvent<unknown> | null, page: number) => {
    setPageNoted(page);
  }, []);

  const handleChangePageNew = useCallback((event: React.ChangeEvent<unknown> | null, page: number) => {
    setPageNew(page);
  }, []);

  const handleRowClick = useCallback((row: any) => {
    setSelectedSlot(row);
    setOpenDialog(true);
  }, []);

  const handleMarkAsNoted = useCallback((row: any) => {
    setSelectedBookingToNote(row);
    setConfirmNotedDialog(true);
  }, []);

  // Optimized: Determine columns with useMemo to prevent recalculation on every render
  const visibleColumns = useMemo(() => {
    if (isMobile) {
      return COLUMNS.filter((col) => ["title", "slotId"].includes(col.id));
    } else if (isTablet) {
      return COLUMNS.filter((col) =>
        [
          "tripId",
          "title",
          "bookingStatus",
          "startDate",
          "bookingAmount",
          "numberofTravellers",
        ].includes(col.id)
      );
    }
    return COLUMNS;
  }, [isMobile, isTablet]);

  // Optimized: Group bookings with useMemo
  const groupedBookings = useMemo(() => ({
    noted: filteredData.filter((booking: any) => booking.isNoted === true).sort((a:BookingDetailsType,b:BookingDetailsType)=>{
       if (status === "CONFIRMED") {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA;
        } else {
          return (
            new Date(b.slotStartDate || 0).getTime() -
            new Date(a.slotStartDate || 0).getTime()
          );
        }
    }),
    new: filteredData.filter((booking: any) => booking.isNoted !== true).sort((a:BookingDetailsType,b:BookingDetailsType)=>{
       if (status === "CONFIRMED") {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA;
        } else {
          return (
            new Date(b.slotStartDate || 0).getTime() -
            new Date(a.slotStartDate || 0).getTime()
          );
        }
    }),
  }), [filteredData, status]);

  // Optimized: Callback for status color to pass down efficiently
  const getStatusColor = useCallback((status: string) => {
    switch (status?.toLowerCase()) {
      case "confirmed": return "success";
      case "pending": return "warning";
      case "cancelled": return "error";
      default: return "primary";
    }
  }, []);

  const handleConfirmNotedClick = async () => {
    if (!selectedBookingToNote) return;
    try {
      const response = await updateBookingNoted(selectedBookingToNote.id, true);
      if (response && response.message === "Item updated successfully") {
        setData((prevData: any[]) =>
          prevData.map((booking) =>
            booking.id === selectedBookingToNote.id
              ? { ...booking, isNoted: true }
              : booking
          )
        );
        setNotificationMessage({ type: "success", message: "Booking marked as noted successfully!" });
        setTimeout(() => setNotificationMessage(null), 3000);
      }
    } catch (error) {
      console.error("Error marking booking as noted:", error);
      setNotificationMessage({ type: "error", message: "An error occurred while updating the booking." });
      setTimeout(() => setNotificationMessage(null), 3000);
    } finally {
      setConfirmNotedDialog(false);
      setSelectedBookingToNote(null);
    }
  };

  useEffect(() => {
    const devFilteredData = data?.filter((slot: any) => !slot?.isPaymentTriggeredByDev);

    if (searchTerm === "") {
      setFilteredData(devFilteredData);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = devFilteredData.filter((slot: any) => {
      return (
        slot.title?.toLowerCase().includes(searchLower) ||
        slot.tripId?.toLowerCase().includes(searchLower) ||
        ((slot.searchIdKey as string) + slot.searchId)
          ?.toLowerCase()
          .includes(searchLower)
      );
    });

    setFilteredData(filtered);
    setPageNoted(1);
    setPageNew(1);
  }, [searchTerm, data]);

  useEffect(() => {
    const fetchBookingBySlotId = async () => {
      if (!userDetails?.userId) return;
      try {
        const response = await bookingByMerchantId(userDetails.userId, status);
        setData(response);
        setFilteredData(response);
        setPageNoted(1);
        setPageNew(1);
      } catch (error) {
        console.error("Error fetching slots:", error);
      }
    };
    fetchBookingBySlotId();
  }, [userDetails?.userId, status]);

  return (
    <Box>
      {notificationMessage && (
        <Alert
          severity={notificationMessage.type}
          onClose={() => setNotificationMessage(null)}
          sx={{ mb: 2 }}
        >
          {notificationMessage.message}
        </Alert>
      )}
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
        mb={3}
        spacing={2}
      >
        <Typography 
          variant="h5" 
          fontWeight="600" 
          color="primary.main"
          sx={{ 
            fontSize: { xs: "1.25rem", md: "1.5rem" },
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -5,
              left: 0,
              width: "40px",
              height: "3px",
              backgroundColor: "primary.main",
              borderRadius: "2px"
            }
          }}
        >
          Individual Booking
        </Typography>
        <TextField
          id="search-field"
          variant="outlined"
          placeholder={isMobile ? "Search trips" : "Search trips by title, ID"}
          value={searchTerm}
          fullWidth={isMobile}
          sx={{
            maxWidth: isMobile ? "100%" : "400px",
            borderRadius: "8px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "& fieldset": {
                borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ color: "#757575", mr: 1 }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <Close
                  sx={{ color: "#757575", cursor: "pointer" }}
                  onClick={() => setSearchTerm("")}
                />
              </InputAdornment>
            ) : null,
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Stack>
      <Paper
        sx={{
          width: "100%",
          mb: 2,
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <Box
          p={1}
          borderBottom={1}
          borderColor="divider"
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isMobile ? "flex-start" : "flex-start"}
          gap={2}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="subtitle1" fontWeight="600" color="#181D27">
              Booking Status
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" sx={{ maxWidth: "100%" }}>
              {["CONFIRMED","ONGOING","COMPLETED", "CANCELLED"].map((tripStatus) => (
                <Chip
                  key={tripStatus}
                  label={tripStatus}
                  onClick={() => setStatus(tripStatus)}
                  color={status === tripStatus ? "primary" : "default"}
                  variant={status === tripStatus ? "filled" : "outlined"}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "8px",
                    fontWeight: status === tripStatus ? 600 : 400,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          <FormControl variant="outlined" sx={{ minWidth: isMobile ? "100%" : "220px" }} size="small">
            <Select
              value={groupMode}
              onChange={(e) => {
                setGroupMode(e.target.value as GroupMode);
                setPageNoted(1);
                setPageNew(1);
              }}
              sx={{ borderRadius: "8px", backgroundColor: "background.paper" }}
            >
              <MenuItem value="all">All Bookings</MenuItem>
              <MenuItem value="trip">Group By Trip Name</MenuItem>
              <MenuItem value="date">Group By Trip Start Date</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* New Bookings Accordion */}
        {groupedBookings.new.length > 0 && (
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: "#F5F5F5",
                borderBottom: "1px solid #EAEAEA",
                "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 },
              }}
            >
              <Chip label={`New Bookings (${groupedBookings.new.length})`} color="primary" variant="filled" size="small" />
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1 }}>
              <Box>
                {groupMode === "all" ? (
                  <BookingListTable 
                    bookings={groupedBookings.new}
                    visibleColumns={visibleColumns}
                    isMobile={isMobile}
                    onRowClick={handleRowClick}
                    getStatusColor={getStatusColor}
                    page={pageNew}
                    onPageChange={handleChangePageNew}
                    searchTerm={searchTerm}
                  />
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {getGroupedBookings(groupedBookings.new, groupMode).map((group) => (
                      <GroupedBookingsTable
                        key={group.groupKey}
                        groupKey={group.groupKey}
                        groupLabel={group.groupLabel}
                        bookings={group.bookings}
                        visibleColumns={visibleColumns}
                        isMobile={isMobile}
                        onRowClick={handleRowClick}
                        getStatusColor={getStatusColor}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Noted Bookings Accordion */}
        {groupedBookings.noted.length > 0 && (
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: "#F5F5F5",
                borderBottom: "1px solid #EAEAEA",
                "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 },
              }}
            >
              <Chip label={`Noted Bookings (${groupedBookings.noted.length})`} color="success" variant="filled" size="small" />
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1 }}>
              <Box>
                {groupMode === "all" ? (
                  <BookingListTable 
                    bookings={groupedBookings.noted}
                    visibleColumns={visibleColumns}
                    isMobile={isMobile}
                    onRowClick={handleRowClick}
                    getStatusColor={getStatusColor}
                    page={pageNoted}
                    onPageChange={handleChangePage}
                    searchTerm={searchTerm}
                  />
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {getGroupedBookings(groupedBookings.noted, groupMode).map((group) => (
                      <GroupedBookingsTable
                        key={group.groupKey}
                        groupKey={group.groupKey}
                        groupLabel={group.groupLabel}
                        bookings={group.bookings}
                        visibleColumns={visibleColumns}
                        isMobile={isMobile}
                        onRowClick={handleRowClick}
                        getStatusColor={getStatusColor}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* No bookings message */}
        {filteredData.length === 0 && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" mb={1}>
              {searchTerm ? "No results found" : "No Booking available"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm
                ? "Try using different search terms"
                : "Try changing the status filter or search term"}
            </Typography>
          </Box>
        )}
      </Paper>

      {openDialog && selectedSlot && (
        <BookingModal
          open={openDialog}
          handleClose={() => setOpenDialog(false)}
          data={selectedSlot || {}}
          showDiscount={organization?.brandName === "escape2explore"}
          onMarkAsNoted={selectedSlot?.isNoted ? undefined : handleMarkAsNoted}
        />
      )}

      {confirmNotedDialog && (
        <ConfirmNotedDialog
          open={confirmNotedDialog}
          tripTitle={selectedBookingToNote?.tripTitle || "Booking"}
          onConfirm={handleConfirmNotedClick}
          onCancel={() => {
            setConfirmNotedDialog(false);
            setSelectedBookingToNote(null);
          }}
        />
      )}
    </Box>
  );
};

export default IndividualBooking;